import { Form } from 'antd'
import { useMemo } from 'react'

import { BUYBACK_FIELD as FIELD } from '../../common/buybackTypes'
import { FinancingScenario } from '../components/BuybackFormFinancingTable/BuybackFormFinancingTable'
import { FINANCING_SETTINGS } from './buybackConst'

/**
 * Hook for calculating financial scenarios for property buyback and market sale.
 *
 * @important Hook can only be used inside a component that is a descendant of a `<Form>` component
 * and contains all the watched `Form.Item` components.
 *
 * `Form.useWatch` returns `undefined` if the corresponding `Form.Item` is not currently mounted on the page.
 *
 * @returns {Object} Object containing:
 * - `loanAmount` - Loan amount (offer price)
 * - `scenarios` - Array of monthly financial scenarios
 * - `rentDurationMonths` - Rent duration in months
 * - `saleDurationMonths` - Sale duration in months
 */

export const useFinancingCalculations = () => {
  const basePropertyValue = Form.useWatch(FIELD.PropertyPrice)
  const offerPrice = Form.useWatch(FIELD.OfferPrice) ?? 0
  const rentDurationMonths = Form.useWatch(FIELD.RentDurationMonths) ?? 0
  const baseBuybackPrice = Form.useWatch(FIELD.BuybackPrice) ?? 0
  const rentPrice = Form.useWatch(FIELD.RentPrice) ?? 0
  const investorRate = Form.useWatch(FIELD.InvestorRate) ?? 0
  const annualPriceGrowth = Form.useWatch(FIELD.MarketPriceAnnualGrowth) ?? 0
  const saleCosts = Form.useWatch(FIELD.MarketSaleCosts) ?? 0
  const saleDurationMonths = Form.useWatch(FIELD.MarketSaleDurationMonths) ?? 0

  const calculations = useMemo(() => {
    const scenarios: FinancingScenario[] = []

    const monthlyInterest = Math.round((offerPrice * (investorRate / 100)) / 12)
    const totalMonthsToDisplay = rentDurationMonths + saleDurationMonths + 3
    const allBreakpoints = [
      ...FINANCING_SETTINGS.buybackBreakpoints,
      rentDurationMonths,
    ].sort((a, b) => a - b)

    for (let month = 1; month <= totalMonthsToDisplay; month++) {
      // loanBalance: Dluh investorovi = nabídková cena + (měsíční úrok × počet měsíců)
      const loanBalance = offerPrice + monthlyInterest * month

      // loanAtSaleBalance: Dluh investorovi v okamžiku prodeje = nabídková cena + (měsíční úrok × (počet měsíců + doba prodeje))
      const loanAtSaleBalance =
        offerPrice + monthlyInterest * (month + saleDurationMonths)

      // rentMonths: Počet měsíců nájmu = minimum z aktuálního měsíce a celkové doby nájmu
      const rentMonths = Math.min(month, rentDurationMonths)

      // totalRentCollected: Celkové vybrané nájemné = měsíční nájem × počet měsíců nájmu
      const totalRentCollected = rentPrice * rentMonths

      // yearsElapsed: Počet uplynulých let = počet měsíců ÷ 12
      const yearsElapsed = month / 12

      // priceGrowthFactor: Faktor růstu ceny = (1 + roční růst v %) ^ počet let
      const priceGrowthFactor = Math.pow(
        1 + annualPriceGrowth / 100,
        yearsElapsed
      )

      // propertyValue: Hodnota nemovitosti = tržní cena nemovitosti × nárůst tržní hodnoty v čase
      const propertyValue = basePropertyValue * priceGrowthFactor

      // salePrice: Prodejní cena = hodnota nemovitosti po odečtení nákladů spojených s jejím prodejem
      const salePrice = propertyValue * (1 - saleCosts / 100)

      // netProceedsMarket: Celkové příjmy z prodeje na trhu = prodejní cena + celkové vybrané nájemné
      const netProceedsMarket = salePrice + totalRentCollected

      // effectiveBuybackMonth: Efektivní měsíc pro výpočet = nejbližší vyšší breakpoint z cenových hladin [12, 18, 24, 30, ...]
      const effectiveBuybackMonth =
        allBreakpoints.find(bp => bp >= month) || month

      // totalIncrease: Celkové navýšení ceny = konečná cena zpětného odkupu - nabídková cena
      const totalIncrease = baseBuybackPrice - offerPrice

      // proportionalIncrease: Proporcionální navýšení = (celkové navýšení × efektivní měsíc) ÷ celková doba nájmu
      const proportionalIncrease =
        rentDurationMonths > 0
          ? Math.round(
              (totalIncrease * effectiveBuybackMonth) / rentDurationMonths
            )
          : 0

      // buybackPriceAtMonth: Cena zpětného odkupu = nabídková cena + proporcionální navýšení podle breakpointu
      const buybackPriceAtMonth = offerPrice + proportionalIncrease

      // netProceedsBuyback: Celkové příjmy ze zpětného odkupu = cena zpětného odkupu + celkové vybrané nájemné
      const netProceedsBuyback = buybackPriceAtMonth + totalRentCollected

      // marketBalance: Bilance při prodeji na trhu = dluh při prodeji - celkové příjmy z prodeje (kladné = ztráta, záporné = zisk)
      const marketBalance = Math.round(netProceedsMarket - loanAtSaleBalance)

      // buybackBalance: Bilance při zpětném odkupu = aktuální dluh - celkové příjmy ze zpětného odkupu (kladné = ztráta, záporné = zisk)
      const buybackBalance = Math.round(netProceedsBuyback - loanBalance)

      // isSafeMarket: Je prodej na trhu rizikový? = marketBalance > 0 (true = riziko ztráty)
      const isSafeMarket = marketBalance > 0

      // isSafeBuyback: Je zpětný odkup rizikový? = buybackBalance > 0 (true = riziko ztráty)
      const isSafeBuyback = buybackBalance > 0

      scenarios.push({
        month,
        loanBalance,
        loanAtSaleBalance,
        totalRentCollected,
        propertyValue,
        salePrice,
        buybackPrice: buybackPriceAtMonth,
        netProceedsMarket,
        netProceedsBuyback,
        isSafeMarket,
        isSafeBuyback,
        marketBalance,
        buybackBalance,
      })
    }

    return {
      loanAmount: offerPrice,
      scenarios,
      rentDurationMonths,
      saleDurationMonths,
    }
  }, [
    offerPrice,
    investorRate,
    rentDurationMonths,
    saleDurationMonths,
    rentPrice,
    annualPriceGrowth,
    basePropertyValue,
    saleCosts,
    baseBuybackPrice,
  ])

  return calculations
}
